import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Download, Loader2, Calendar, Filter, DollarSign, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate list of months for specific month selector
const generateMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = subMonths(now, i);
    options.push({
      value: format(date, 'yyyy-MM'),
      label: format(date, 'MMMM yyyy'),
    });
  }
  return options;
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
};

const MarginBadge = ({ margin, marginPct }) => {
  if (margin > 0) {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
        <TrendingUp className="w-3 h-3" />
        {marginPct.toFixed(1)}%
      </span>
    );
  } else if (margin < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
        <TrendingDown className="w-3 h-3" />
        {marginPct.toFixed(1)}%
      </span>
    );
  }
  return <span className="text-muted-foreground">0%</span>;
};

export default function FinancialReportPage() {
  const [report, setReport] = useState({ projects: [], summary: {} });
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [timePeriod, setTimePeriod] = useState('all');
  const [specificMonth, setSpecificMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  const monthOptions = generateMonthOptions();

  // Calculate date range based on selected filter
  const getDateRange = useCallback(() => {
    const now = new Date();
    
    switch (timePeriod) {
      case 'this_month':
        return {
          start_date: format(startOfMonth(now), 'yyyy-MM-dd'),
          end_date: format(endOfMonth(now), 'yyyy-MM-dd'),
        };
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        return {
          start_date: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
          end_date: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
        };
      case 'specific_month':
        const [year, month] = specificMonth.split('-');
        const specificDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        return {
          start_date: format(startOfMonth(specificDate), 'yyyy-MM-dd'),
          end_date: format(endOfMonth(specificDate), 'yyyy-MM-dd'),
        };
      case 'custom':
        return {
          start_date: customStartDate || undefined,
          end_date: customEndDate || undefined,
        };
      case 'all':
      default:
        return {
          start_date: undefined,
          end_date: undefined,
        };
    }
  }, [timePeriod, specificMonth, customStartDate, customEndDate]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const { start_date, end_date } = getDateRange();
      const params = {};
      if (start_date) params.start_date = start_date;
      if (end_date) params.end_date = end_date;
      
      const response = await axios.get(`${API_URL}/api/reports/financials`, { params });
      setReport(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin only.');
      } else {
        toast.error('Failed to load financial report');
      }
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Get display label for current filter
  const getFilterLabel = () => {
    switch (timePeriod) {
      case 'this_month':
        return format(new Date(), 'MMMM yyyy');
      case 'last_month':
        return format(subMonths(new Date(), 1), 'MMMM yyyy');
      case 'specific_month':
        const [year, month] = specificMonth.split('-');
        return format(new Date(parseInt(year), parseInt(month) - 1, 1), 'MMMM yyyy');
      case 'custom':
        if (customStartDate && customEndDate) {
          return `${customStartDate} to ${customEndDate}`;
        } else if (customStartDate) {
          return `From ${customStartDate}`;
        } else if (customEndDate) {
          return `Until ${customEndDate}`;
        }
        return 'Custom Range';
      case 'all':
      default:
        return 'All Time';
    }
  };

  const downloadCSV = async () => {
    try {
      const { start_date, end_date } = getDateRange();
      const params = {};
      if (start_date) params.start_date = start_date;
      if (end_date) params.end_date = end_date;
      
      const response = await axios.get(`${API_URL}/api/reports/export/financials`, {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `financial_report_${getFilterLabel().replace(/ /g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Financial report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const { summary } = report;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <DollarSign className="w-6 h-6" />
            Financial Report
          </h1>
          <p className="text-muted-foreground">Project revenue, cost, and margin analysis</p>
        </div>
        <Button onClick={downloadCSV} variant="outline" data-testid="download-financial-report">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Time Period Filter */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Time Period Filter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2 min-w-[180px]">
              <Label htmlFor="time-period">Period</Label>
              <Select value={timePeriod} onValueChange={setTimePeriod}>
                <SelectTrigger id="time-period" data-testid="financial-time-period-select">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="last_month">Last Month</SelectItem>
                  <SelectItem value="specific_month">Specific Month</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {timePeriod === 'specific_month' && (
              <div className="space-y-2 min-w-[180px]">
                <Label htmlFor="specific-month">Month</Label>
                <Select value={specificMonth} onValueChange={setSpecificMonth}>
                  <SelectTrigger id="specific-month">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {timePeriod === 'custom' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="start-date">Start Date</Label>
                  <Input
                    id="start-date"
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-[160px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-date">End Date</Label>
                  <Input
                    id="end-date"
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-[160px]"
                  />
                </div>
              </>
            )}

            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted px-3 py-2 rounded-md">
              <Calendar className="w-4 h-4" />
              <span>Showing: <strong className="text-foreground">{getFilterLabel()}</strong></span>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Planned Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">Planned Financials</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                      {formatCurrency(summary.total_planned_revenue || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">Revenue</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <p className="text-lg font-bold text-orange-600 dark:text-orange-400">
                      {formatCurrency(summary.total_planned_cost || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">Cost</p>
                  </div>
                  <div className={`text-center p-3 rounded-lg ${
                    (summary.total_planned_margin || 0) >= 0 
                      ? 'bg-emerald-50 dark:bg-emerald-900/20' 
                      : 'bg-red-50 dark:bg-red-900/20'
                  }`}>
                    <p className={`text-lg font-bold ${
                      (summary.total_planned_margin || 0) >= 0 
                        ? 'text-emerald-600 dark:text-emerald-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {formatCurrency(summary.total_planned_margin || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Margin ({summary.total_planned_margin_pct || 0}%)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actual Summary */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">Actual Financials</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                      {formatCurrency(summary.total_actual_revenue || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">Revenue</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                    <p className="text-lg font-bold text-orange-600 dark:text-orange-400">
                      {formatCurrency(summary.total_actual_cost || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">Cost</p>
                  </div>
                  <div className={`text-center p-3 rounded-lg ${
                    (summary.total_actual_margin || 0) >= 0 
                      ? 'bg-emerald-50 dark:bg-emerald-900/20' 
                      : 'bg-red-50 dark:bg-red-900/20'
                  }`}>
                    <p className={`text-lg font-bold ${
                      (summary.total_actual_margin || 0) >= 0 
                        ? 'text-emerald-600 dark:text-emerald-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      {formatCurrency(summary.total_actual_margin || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Margin ({summary.total_actual_margin_pct || 0}%)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Variance Summary */}
          {(summary.variance_revenue !== 0 || summary.variance_cost !== 0 || summary.variance_margin !== 0) && (
            <Card className="border-amber-200 dark:border-amber-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Variance (Actual - Planned)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className={`text-lg font-bold ${(summary.variance_revenue || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {(summary.variance_revenue || 0) >= 0 ? '+' : ''}{formatCurrency(summary.variance_revenue || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">Revenue Variance</p>
                  </div>
                  <div>
                    <p className={`text-lg font-bold ${(summary.variance_cost || 0) <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {(summary.variance_cost || 0) >= 0 ? '+' : ''}{formatCurrency(summary.variance_cost || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">Cost Variance</p>
                  </div>
                  <div>
                    <p className={`text-lg font-bold ${(summary.variance_margin || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {(summary.variance_margin || 0) >= 0 ? '+' : ''}{formatCurrency(summary.variance_margin || 0)}
                    </p>
                    <p className="text-xs text-muted-foreground">Margin Variance</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Project Details Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium">Project Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              {report.projects.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No project data available for the selected period
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead rowSpan={2} className="border-r">Project</TableHead>
                        <TableHead rowSpan={2} className="border-r">Employee</TableHead>
                        <TableHead rowSpan={2} className="text-right border-r">Rates</TableHead>
                        <TableHead colSpan={4} className="text-center border-r bg-blue-50 dark:bg-blue-900/20">Planned</TableHead>
                        <TableHead colSpan={4} className="text-center bg-emerald-50 dark:bg-emerald-900/20">Actual</TableHead>
                      </TableRow>
                      <TableRow>
                        <TableHead className="text-right bg-blue-50 dark:bg-blue-900/20">Hours</TableHead>
                        <TableHead className="text-right bg-blue-50 dark:bg-blue-900/20">Revenue</TableHead>
                        <TableHead className="text-right bg-blue-50 dark:bg-blue-900/20">Cost</TableHead>
                        <TableHead className="text-right border-r bg-blue-50 dark:bg-blue-900/20">Margin</TableHead>
                        <TableHead className="text-right bg-emerald-50 dark:bg-emerald-900/20">Hours</TableHead>
                        <TableHead className="text-right bg-emerald-50 dark:bg-emerald-900/20">Revenue</TableHead>
                        <TableHead className="text-right bg-emerald-50 dark:bg-emerald-900/20">Cost</TableHead>
                        <TableHead className="text-right bg-emerald-50 dark:bg-emerald-900/20">Margin</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.projects.map((project) => (
                        <TableRow key={project.project_id}>
                          <TableCell className="border-r">
                            <div>
                              <div className="font-medium">{project.project_name}</div>
                              <div className="text-xs text-muted-foreground">
                                {project.project_code} {project.client_name && `• ${project.client_name}`}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="border-r">{project.employee_name || '-'}</TableCell>
                          <TableCell className="text-right border-r text-xs">
                            <div>Bill: {formatCurrency(project.billable_rate)}/hr</div>
                            <div className="text-muted-foreground">Pay: {formatCurrency(project.employee_pay_rate)}/hr</div>
                          </TableCell>
                          {/* Planned */}
                          <TableCell className="text-right">{project.planned_hours}</TableCell>
                          <TableCell className="text-right">{formatCurrency(project.planned_revenue)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(project.planned_cost)}</TableCell>
                          <TableCell className="text-right border-r">
                            <div>{formatCurrency(project.planned_margin)}</div>
                            <MarginBadge margin={project.planned_margin} marginPct={project.planned_margin_pct} />
                          </TableCell>
                          {/* Actual */}
                          <TableCell className="text-right">{project.actual_hours}</TableCell>
                          <TableCell className="text-right">{formatCurrency(project.actual_revenue)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(project.actual_cost)}</TableCell>
                          <TableCell className="text-right">
                            <div>{formatCurrency(project.actual_margin)}</div>
                            <MarginBadge margin={project.actual_margin} marginPct={project.actual_margin_pct} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
